import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  CreditCard, 
  Wallet, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Plus,
  Search,
  Filter,
  Eye,
  UserPlus,
  DollarSign,
  Clock,
  TrendingUp
} from 'lucide-react';

const WorkflowManagement = () => {
  const { t, i18n } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    userId: '',
    amount: '',
    description: ''
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const isRTL = i18n.language === 'ar';

  const steps = [
    { id: 1, title: t('workflow.steps.users'), description: t('workflow.steps.usersDesc'), icon: Users },
    { id: 2, title: t('workflow.steps.payments'), description: t('workflow.steps.paymentsDesc'), icon: CreditCard },
    { id: 3, title: t('workflow.steps.wallet'), description: t('workflow.steps.walletDesc'), icon: Wallet },
    { id: 4, title: t('workflow.steps.sessions'), description: t('workflow.steps.sessionsDesc'), icon: Calendar },
    { id: 5, title: t('workflow.steps.assignment'), description: t('workflow.steps.assignmentDesc'), icon: UserPlus },
    { id: 6, title: t('workflow.steps.attendance'), description: t('workflow.steps.attendanceDesc'), icon: CheckCircle }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, paymentsRes, walletsRes, sessionsRes] = await Promise.all([
        axios.get('/api/users', { headers }),
        axios.get('/api/payments', { headers }),
        axios.get('/api/wallets', { headers }),
        axios.get('/api/sessions', { headers })
      ]);

      setUsers(usersRes.data.users || []);
      setPayments(paymentsRes.data.payments || []);
      setWallets(walletsRes.data.wallets || []);
      setSessions(sessionsRes.data?.sessions || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const createPayment = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/payments', {
        userId: parseInt(paymentData.userId),
        amount: parseFloat(paymentData.amount),
        type: 'Session',
        description: paymentData.description || `Payment of ${paymentData.amount} DA`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('common.success'));
      setShowPaymentModal(false);
      setPaymentData({ userId: '', amount: '', description: '' });
      fetchAllData();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(t('common.error'));
    }
  };

  const assignUserToSession = async (sessionId, userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/sessions/${sessionId}/assign-user`, {
        userId: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('common.success'));
      fetchAllData();
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error(t('common.error'));
    }
  };

  const getRoleColor = (role) => {
    const roleNum = typeof role === 'string' ? parseInt(role) : role;
    switch (roleNum) {
      case 0: return 'bg-blue-100 text-blue-800';
      case 1: return 'bg-purple-100 text-purple-800';
      case 2: return 'bg-green-100 text-green-800';
      case 3: return 'bg-orange-100 text-orange-800';
      case 4: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const shouldShowWalletBalance = (role) => {
    const roleNum = typeof role === 'string' ? parseInt(role) : role;
    return roleNum === 0 || roleNum === 1;
  };

  const getUserWalletBalance = (userId) => {
    const wallet = wallets.find(w => w.userId === userId);
    return wallet ? wallet.points : 0;
  };

  const filteredUsers = users.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 1: // Users
        return (
          <div className="space-y-6 p-4 sm:p-6 lg:p-8" key={refreshKey}>
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-medium">{t('workflow.usersManagement')}</h3>
              <div className="text-sm text-gray-500">
                {t('workflow.totalUsers')}: {users.length}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className={isRTL ? 'text-right' : 'text-left'}>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.role')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-gray-600" />
                            </div>
                          </div>
                          <div className={isRTL ? 'mr-4' : 'ml-4'}>
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {t(`roles.${user.role}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status === 0 ? t('common.active') : t('common.deactivated')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowPaymentModal(true);
                          }}
                          className={`text-blue-600 hover:text-blue-900 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          <DollarSign className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                          {t('common.addPayment')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 2: // Payments
        return (
          <div className="space-y-6 p-4 sm:p-6 lg:p-8" key={refreshKey}>
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-medium">{t('workflow.paymentsManagement')}</h3>
              <div className="text-sm text-gray-500">
                {t('workflow.totalPayments')}: {payments.length}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className={isRTL ? 'text-right' : 'text-left'}>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.amount')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.date')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {payment.user?.firstName} {payment.user?.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{payment.user?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.amount} DA
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          payment.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(payment.createdAt).toLocaleDateString(i18n.language)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 3: // Wallet Credits
        return (
          <div className="space-y-6 p-4 sm:p-6 lg:p-8" key={refreshKey}>
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-medium">{t('workflow.walletBalances')}</h3>
              <div className="text-sm text-gray-500">
                {t('workflow.totalWallets')}: {wallets.length}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className={isRTL ? 'text-right' : 'text-left'}>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.role')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.amount')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {wallets.map((wallet) => {
                    const user = users.find(u => u.id === wallet.userId);
                    return (
                      <tr key={wallet.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {wallet.userName}
                          </div>
                          <div className="text-sm text-gray-500">{wallet.userEmail}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}>
                            {t(`roles.${user?.role}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Wallet className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className={`text-sm font-medium ${
                              wallet.points >= 1 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {wallet.points.toFixed(2)} {t('workflow.points')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            wallet.points >= 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {wallet.points >= 1 ? t('workflow.sufficient') : t('workflow.insufficient')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 4: // Sessions
        return (
          <div className="space-y-6 p-4 sm:p-6 lg:p-8" key={refreshKey}>
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-medium">{t('workflow.sessionsManagement')}</h3>
              <div className="text-sm text-gray-500">
                {t('workflow.totalSessions')}: {sessions.length}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className={isRTL ? 'text-right' : 'text-left'}>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.type')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.date')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('attendance.participant')}</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map((session) => (
                    <tr key={session.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {session.title}
                        </div>
                        <div className="text-sm text-gray-500">{session.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(session.date).toLocaleDateString(i18n.language)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.startTime} - {session.endTime}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {session.participants?.length || 0} {t('workflow.participants')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {session.status === 'Scheduled' ? t('workflow.scheduled') : session.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 5: // Assignment
        return (
          <div className="space-y-6 p-4 sm:p-6 lg:p-8" key={refreshKey}>
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-medium">{t('workflow.sessionAssignment')}</h3>
              <div className="text-sm text-gray-500">
                {t('workflow.assignmentHint')}
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Users with sufficient balance */}
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className={`text-lg font-medium mb-4 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Users className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('workflow.readyForAssignment')}
                </h4>
                <div className="space-y-3">
                  {users.filter(user => {
                    const balance = getUserWalletBalance(user.id);
                    return shouldShowWalletBalance(user.role) && balance >= 1;
                  }).map((user) => (
                    <div key={user.id} className={`flex items-center justify-between p-3 bg-green-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <div className="font-medium">{user.firstName} {user.lastName}</div>
                        <div className="text-sm text-gray-500">{getUserWalletBalance(user.id).toFixed(2)} {t('workflow.points')}</div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        {t(`roles.${user.role}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sessions */}
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className={`text-lg font-medium mb-4 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Calendar className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('workflow.availableSessions')}
                </h4>
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div key={session.id} className={`p-3 bg-blue-50 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                      <div className="font-medium">{session.title}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(session.date).toLocaleDateString(i18n.language)} - {session.startTime}
                      </div>
                      <div className="text-sm text-gray-500">
                        {session.participants?.length || 0} {t('workflow.participants')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 6: // Attendance
        return (
          <div className="space-y-6 p-4 sm:p-6 lg:p-8" key={refreshKey}>
            <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-medium">{t('workflow.attendanceManagement')}</h3>
              <div className="text-sm text-gray-500">
                {t('workflow.attendanceDesc')}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('workflow.attendanceReady')}</h3>
                <p className="text-gray-500 mb-4 whitespace-pre-line">
                  {t('workflow.attendanceHint')}
                </p>
                <div className="text-sm text-gray-400 whitespace-pre-line">
                  {t('workflow.attendanceRules')}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`p-6 bg-gray-100 min-h-screen ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('workflow.title')}</h1>
        <p className="text-gray-600">{t('workflow.subtitle')}</p>
      </div>

      {/* Steps Navigation */}
      <div className="mb-8">
        <div className={`flex space-x-4 overflow-x-auto pb-4 ${isRTL ? 'space-x-reverse' : ''}`}>
          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = activeStep === step.id;
            const isCompleted = activeStep > step.id;
            
            return (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg border-2 transition-all min-w-[200px] ${isRTL ? 'space-x-reverse' : ''} ${
                  isActive 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : isCompleted 
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <div className="font-medium whitespace-nowrap">{step.title}</div>
                  <div className="text-xs opacity-75">{step.description}</div>
                </div>
                {isCompleted && <CheckCircle className={`w-4 h-4 ${isRTL ? 'mr-auto' : 'ml-auto'}`} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-500">{t('common.loading')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="p-6">
              {renderStepContent()}
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : ''}`}>
                {t('workflow.addPaymentFor', { name: `${selectedUser.firstName} ${selectedUser.lastName}` })}
              </h3>
              
              <div className="space-y-4">
                <div className={isRTL ? 'text-right' : ''}>
                  <label className="block text-sm font-medium text-gray-700">{t('workflow.amountDA')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('workflow.enterAmountDA')}
                  />
                </div>
                
                <div className={isRTL ? 'text-right' : ''}>
                  <label className="block text-sm font-medium text-gray-700">{t('common.description')}</label>
                  <input
                    type="text"
                    value={paymentData.description}
                    onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('common.description')}
                  />
                </div>
              </div>
              
              <div className={`flex justify-end space-x-3 mt-6 ${isRTL ? 'space-x-reverse' : ''}`}>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    setPaymentData({...paymentData, userId: selectedUser.id});
                    createPayment();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {t('common.submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowManagement;

